import { showAlertCallout } from "@/shared/alerts";
import type { FormState } from "@/shared/types/workflow/workflow-form.types";
import { isCommentFieldValue } from "@/shared/utils/field-value-type-guards";
import { getGetControlResultAttachmentsQueryKey } from "@/spz-api/control-result-comments/control-result-comments";
import {
  getGetControlResultActivitiesQueryKey,
  getGetControlResultListPaginatedQueryKey,
  getGetControlResultQueryKey,
  useUpdateWorkflowStep,
} from "@/spz-api/control-results/control-results";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useUploadCommentAttachmentMutation } from "./use-upload-comment-attachment.mutation";

export const useWorkflowStepUpdateWorkflowTransition = ({
  controlId,
  controlResultId,
}: {
  controlId: string;
  controlResultId: string;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useUpdateWorkflowStep({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetControlResultListPaginatedQueryKey(controlId),
        });
        queryClient.invalidateQueries({
          queryKey: getGetControlResultQueryKey(controlId, controlResultId),
        });
        queryClient.invalidateQueries({
          queryKey: getGetControlResultActivitiesQueryKey(
            controlId,
            controlResultId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: getGetControlResultAttachmentsQueryKey(
            controlId,
            controlResultId,
          ),
        });
      },
      onError: () => {},
    },
  });

  const uploadCommentAttachmentMutation = useUploadCommentAttachmentMutation({
    controlId,
    controlResultId,
  });

  /**
   * Uploads a file and returns its ID
   */
  const uploadFile = async (file: File): Promise<string | undefined> => {
    const result = await uploadCommentAttachmentMutation.mutateAsync({ file });
    return result?.id;
  };

  /**
   * Processes formData to:
   * 1. Detect all File instances (in comment fields or file_upload fields)
   * 2. Upload them and replace with their IDs
   * 3. Return processed data ready for submission
   */
  const processFormData = async (
    rawData: Record<string, unknown> | null,
  ): Promise<Record<string, unknown>> => {
    if (!rawData) {
      return {};
    }

    const processedData: Record<string, unknown> = {};

    for (const [fieldName, fieldValue] of Object.entries(rawData)) {
      // Case 1: Direct File (file_upload field)
      if (fieldValue instanceof File) {
        const uploadedId = await uploadFile(fieldValue);
        if (uploadedId) {
          processedData[fieldName] = uploadedId;
        }
        continue;
      }

      // Case 2: CommentFieldValue (comment field with potential attachment)
      if (isCommentFieldValue(fieldValue)) {
        const processedComment: Record<string, unknown> = {
          content: fieldValue.content,
        };

        // Upload attachment if present
        if (fieldValue.attachment instanceof File) {
          const uploadedId = await uploadFile(fieldValue.attachment);
          if (uploadedId) {
            processedComment.attachment = uploadedId;
          }
        } else if (fieldValue.attachment === null) {
          // Explicitly preserve null
          processedComment.attachment = null;
        }

        processedData[fieldName] = processedComment;
        continue;
      }

      // Case 3: Other values (pass through)
      processedData[fieldName] = fieldValue;
    }

    return processedData;
  };

  /**
   * Handles workflow transition submission.
   * Automatically detects and uploads all File instances in formData,
   * then submits the processed data.
   *
   * @param transitionId - The id of the transition (from the clicked button)
   * @param formData - The form data to submit (can be null or object)
   */
  const handleTransitionSubmit = async (
    transitionId: string,
    formData: FormState | null,
  ) => {
    // Ensure formData is an object or null
    const rawData = formData && typeof formData === "object" ? formData : null;

    try {
      // Process formData: upload files and get their IDs
      const processedData = await processFormData(rawData);

      await mutate({
        controlId,
        resultId: controlResultId,
        data: {
          transitionId,
          data: processedData,
        },
      });
    } catch (error) {
      // Fallback generic error
      showAlertCallout({
        type: "critical",
        title: t("generic_error"),
      });
    }
  };

  return {
    isPending,
    mutate: handleTransitionSubmit,
  };
};
